import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { FLAG_REASON_MAX_LEN, REVIEW_PATHS } from "../../reviews/contract";
import { reviewErrorText } from "../../reviews/errorText";
import { REVIEWS } from "../../constants/testIds/reviews";

// A flag is a request for a human to look, so the reason must not be blank — and that is
// the WHOLE rule, because it is the server's rule (`REVIEW_INVALID` on an empty reason,
// contract §1). The form refuses exactly what the API refuses; a stricter local floor
// would forbid a value the API accepts, and the two would disagree about one input.
const hasReason = (value) => value.trim().length > 0;

/**
 * "Ask the platform to look at this review."
 *
 * ONE dialog for the whole console, opened with the row it is about — the contract's
 * `adminFlagReason` / `adminFlagSubmit` ids are not parameterised, which is the same
 * statement: only one flag is ever being written.
 *
 * A REASON IS REQUIRED, because the ONLY thing this write produces is a sentence an operator
 * will read in the moderation queue. A flag with no reason moves the review nowhere and
 * costs the practice its one flag (the server CASes on `flag: null`, so there is no second
 * try). The minimum is enforced here AND by the server's `REVIEW_INVALID`; a form laxer than
 * the API only moves the rejection to an opaque refusal.
 *
 * @param {?object} review the `owner_review` being flagged; `null` closes the dialog
 * @param {function} t `useT("admin")`
 * @param {function} tv `useT("common")`
 * @param {(didFlag: boolean) => void} onClose
 */
export default function FlagDialog({ review, t, tv, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Every opening starts from an empty field: a reason typed for one review must never be
  // carried into the dialog for another.
  useEffect(() => {
    if (review) {
      setReason("");
      setBusy(false);
    }
  }, [review]);

  const submit = async () => {
    const body = reason.trim();
    if (!hasReason(body) || busy) return;
    setBusy(true);
    try {
      await api.post(REVIEW_PATHS.tenantFlag(review.id), { reason: body });
      toast.success(t("rev.flag.saved"));
      onClose(true);
    } catch (e) {
      toast.error(reviewErrorText(e, { t, tv, fallback: tv("errors.saveFailed") }));
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!review} onOpenChange={(open) => { if (!open) onClose(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("rev.flag.title")}</DialogTitle>
          <DialogDescription>{t("rev.flag.desc")}</DialogDescription>
        </DialogHeader>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={FLAG_REASON_MAX_LEN}
          rows={4}
          disabled={busy}
          placeholder={t("rev.flag.placeholder")}
          data-testid={REVIEWS.adminFlagReason}
          className="w-full rounded-xl border border-eh-border-soft bg-white p-3 text-sm text-eh-ink focus:outline-none focus:border-eh-sage-deep"
        />
        <DialogFooter>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 h-9 rounded-full border border-eh-border-soft text-sm text-eh-ink"
          >
            {tv("actions.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !hasReason(reason)}
            data-testid={REVIEWS.adminFlagSubmit}
            className="px-4 h-9 rounded-full bg-amber-700 text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? t("rev.flag.submitting") : t("rev.flag.submit")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
